import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import React, { useEffect, useRef } from 'react';

export type GlowSeverity = 'error' | 'success' | 'info';

const ICONS: Record<GlowSeverity, React.ElementType> = {
    error: ErrorOutlineIcon,
    success: CheckCircleOutlineIcon,
    info: InfoOutlinedIcon,
};

/**
 * Notice with a glowing border that rotates around it, coloured by severity.
 * Same look as the failure notice in victoria-ui; the icon and caption are
 * built in.
 *
 * Forwards its ref to the root element: transitions like MUI's Snackbar
 * `Grow` clone their child and attach a ref to measure the DOM node, which
 * silently fails (and later throws reading `scrollTop` off `null`) on a
 * plain function component.
 */
export const GlowAlert = React.forwardRef<
    HTMLDivElement,
    {
        severity?: GlowSeverity;
        children: React.ReactNode;
        sx?: SxProps<Theme>;
    }
>(({ severity = 'error', children, sx }, forwardedRef) => {
    const Icon = ICONS[severity];
    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        let angle = 0;
        let animationId: number;

        const rotate = () => {
            angle = (angle + 3) % 360;
            ref.current?.style.setProperty('--angle', `${angle}deg`);
            animationId = requestAnimationFrame(rotate);
        };

        rotate();
        return () => cancelAnimationFrame(animationId);
    }, []);

    return (
        <Box
            ref={(node: HTMLDivElement | null) => {
                ref.current = node;
                if (typeof forwardedRef === 'function') forwardedRef(node);
                else if (forwardedRef)
                    (
                        forwardedRef as React.MutableRefObject<HTMLDivElement | null>
                    ).current = node;
            }}
            role="alert"
            sx={[
                (theme: Theme) => {
                    const fill =
                        theme.palette.mode === 'dark'
                            ? 'rgba(0, 0, 0, 1)'
                            : 'rgba(255, 255, 255, 1)';
                    return {
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 0.75,
                        px: 1.25,
                        py: 0.75,
                        borderRadius: 2,
                        border: '1px solid transparent',
                        background: `linear-gradient(${fill}, ${fill}) padding-box,
                                    conic-gradient(from var(--angle, 0deg), transparent, ${theme.palette[severity].main}, transparent) border-box`,
                        backdropFilter: 'blur(6px)',
                        WebkitBackdropFilter: 'blur(6px)',
                        boxShadow: `0 0 16px ${theme.palette[severity].main}40`,
                        color: theme.palette[severity].main,
                    };
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        >
            <Icon sx={{ fontSize: 16 }} />
            <Typography variant="caption" sx={{ color: 'inherit' }}>
                {children}
            </Typography>
        </Box>
    );
});

GlowAlert.displayName = 'GlowAlert';
